import React from 'react';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { addMonths, format, isAfter, startOfDay, startOfToday, subDays } from 'date-fns';
import { SUBSCRIPTION_TERM, getSubscriptionTermLabel, getSubscriptionTermLengthInMonths, getSubscriptionTermRenewalWording } from 'src/constants/subscriptionTerm';
import useStyles from './styles';
const TERM_OPTIONS = [SUBSCRIPTION_TERM.Annual, SUBSCRIPTION_TERM.Monthly, SUBSCRIPTION_TERM.SemiAnnual];
const INVOICE_DAY = 5;
const ORDINAL_SUFFIXES = {
  one: 'st',
  two: 'nd',
  few: 'rd',
  other: 'th'
};
const toOrdinal = day => "".concat(day).concat(ORDINAL_SUFFIXES[new Intl.PluralRules('en-US', {
  type: 'ordinal'
}).select(day)]);
export const getFirstInvoiceDate = startDate => {
  const sameMonth = new Date(startDate.getFullYear(), startDate.getMonth(), INVOICE_DAY);
  return isAfter(sameMonth, startOfDay(startDate)) ? sameMonth : addMonths(sameMonth, 1);
};
const getBillingStartWording = startDate => {
  const day = toOrdinal(INVOICE_DAY);
  if (!startDate || !isAfter(startOfDay(startDate), startOfToday())) return "Billing starts on the next ".concat(day, ".");
  const firstInvoiceDate = getFirstInvoiceDate(startDate);
  const year = firstInvoiceDate.getFullYear() === startOfToday().getFullYear() ? '' : ", ".concat(firstInvoiceDate.getFullYear());
  return "Billing starts on ".concat(format(firstInvoiceDate, 'MMMM'), " ").concat(day).concat(year, ".");
};
export const TERM_DATE_FORMAT = 'MM/dd/yyyy';
const DATE_FORMAT = TERM_DATE_FORMAT;
const MAX_TERMS = 1200;
export const pluralizeMonths = count => "".concat(count, " month").concat(count === 1 ? '' : 's');
export const getMonthsBetween = (from, to) => {
  if (!from || !to) return 0;
  return Math.max(0, (to.getFullYear() - from.getFullYear()) * 12 + to.getMonth() - from.getMonth());
};
export const getRenewalDate = (startDate, term) => {
  const lengthInMonths = getSubscriptionTermLengthInMonths(term);
  if (!startDate || !lengthInMonths) return null;
  const today = startOfToday();
  let terms = 1;
  let renewal = addMonths(startDate, lengthInMonths);
  while (!isAfter(renewal, today) && terms < MAX_TERMS) {
    terms += 1;
    renewal = addMonths(startDate, lengthInMonths * terms);
  }
  return renewal;
};
export const getCommitmentEndDate = (startDate, term) => {
  if (term === SUBSCRIPTION_TERM.Monthly) return null;
  const renewal = getRenewalDate(startDate, term);
  return renewal ? subDays(renewal, 1) : null;
};
export const SubscriptionTerm = _ref => {
  let value = _ref.value,
    onChange = _ref.onChange,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled;
  const classes = useStyles();
  return /*#__PURE__*/React.createElement(Box, {
    className: classes.field
  }, /*#__PURE__*/React.createElement(TextField, {
    select: true,
    label: "Term",
    value: value !== null && value !== void 0 ? value : '',
    onChange: event => onChange(event.target.value),
    disabled: disabled,
    fullWidth: true
  }, TERM_OPTIONS.map(term => /*#__PURE__*/React.createElement(MenuItem, {
    key: term,
    value: term
  }, getSubscriptionTermLabel(term)))));
};
export const parseDateOnly = value => value ? new Date("".concat(value, "T00:00:00")) : null;
export const SubscriptionTermSummary = _ref2 => {
  var _parseDateOnly;
  let term = _ref2.term,
    startDate = _ref2.startDate,
    storedCommitmentEndDate = _ref2.storedCommitmentEndDate;
  const classes = useStyles();
  const commitmentEndDate = (_parseDateOnly = parseDateOnly(storedCommitmentEndDate)) !== null && _parseDateOnly !== void 0 ? _parseDateOnly : getCommitmentEndDate(startDate, term);
  const renewalWording = getSubscriptionTermRenewalWording(term);
  if (!renewalWording) return null;
  return /*#__PURE__*/React.createElement(Box, {
    className: classes.summary
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption"
  }, commitmentEndDate ? "Term ends ".concat(format(commitmentEndDate, DATE_FORMAT), ". ").concat(renewalWording) : "".concat(renewalWording, " ").concat(getBillingStartWording(startDate))));
};